import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Avatar,
    List,
    ListItemIcon,
    ListItemText,
    Button,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    InputAdornment,
    ListItemButton,
    Drawer,
    Checkbox,
    Menu,
    MenuItem
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BarChartIcon from '@mui/icons-material/BarChart';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import HelpIcon from '@mui/icons-material/Help';
import { signOut } from '../../utilis/signout';
import { useContext } from 'react';
import AppContext from '../../context/AppContext';
import { setSelectedAccount, setFilterDateUpdate, setFacilityLoaded, setEndDateOfADT, setPercentageBy } from '../../store/reducers/common.slice';
import { removeAllActiveFacilities } from '../../store/reducers/activeFacilities.slice';
import { setIsComparisonRangeSet, setManuallyClickComparison, setRemoveCompareFilter, setResetComparison, setRemoveComparisonRanges, setcomparingAgainstDifferentFacility } from '../../store/reducers/comparisonReducers/comparison.slice';
import { setDefaultHospitalData, setLoading as setHospitalLoading } from '../../store/reducers/hospital.slice';
import { setDefaultAdmissionData, setLoading as setAdmissionLoading } from '../../store/reducers/admission.slice';
import { setDefaultCommunityTransferData, setLoading as setCommunityLoading } from '../../store/reducers/community-transfer.slice';
import { setDefaultDeceasedData, setLoading as setDescentLoading } from '../../store/reducers/deceased.slice';
import { setDefaultOverAllData, setLoading as setOverallLoading } from '../../store/reducers/overall.slice';
import { SET_ACCOUNTS, SET_FACILITIES } from '../../store/types';
import { resetAllCards } from '../hooks/useResetDynamicCards';
import { percentageByEnum } from '../../data/common.data';
import TempLogo from "../../assets/svgs/temp-logo.svg";
import { PAGE_TYPE } from "../../types/pages.type";
import _ from "lodash";
import useSetParamToURL from "../hooks/useSetParamToURL";
import useTutorialTrigger from '../../hooks/useTutorialTrigger';
import { TUTORIAL_FEATURES } from '../../config/tutorials.config';
import { setFacilityId } from "../../utilis/facilityStorage";

const MobileApp = ({ open, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { setPreviousParams, setParamToURL } = useSetParamToURL();
    const { timeLogControl } = useContext(AppContext);
    const { triggerTutorial, trackFeatureUsage } = useTutorialTrigger();

    // Mobile-specific state
    const [mobileSelectedAccount, setMobileSelectedAccount] = useState(null);
    const [mobileSelectedFacilities, setMobileSelectedFacilities] = useState([]);
    const [showAccountDialog, setShowAccountDialog] = useState(false);
    const [showFacilityDialog, setShowFacilityDialog] = useState(false);
    const [accountSearchText, setAccountSearchText] = useState('');
    const [facilitySearchText, setFacilitySearchText] = useState('');
    const [selectAllFacilities, setSelectAllFacilities] = useState(false);
    const [hasCompletedSetup, setHasCompletedSetup] = useState(false);
    // Profile menu state
    const [profileAnchorEl, setProfileAnchorEl] = useState(null);
    const profileOpen = Boolean(profileAnchorEl);

    const handleProfileClick = (event) => {
        setProfileAnchorEl(event.currentTarget);
    };

    const handleProfileClose = () => {
        setProfileAnchorEl(null);
    };
    // Redux selectors
    const { auth, account: accounts, facility } = useSelector(({ auth, account, facility }) => ({ auth, account, facility }));
    const facilities = useSelector((state) => state.facility);
    const activeFacilitiesComparison = useSelector((state) => state.comparison.activeComparisonFacilitiesId);
    const [initialLoad, setInitialLoad] = useState(true);
    // Show navigation menu after facility selection
    const showNavigation = mobileSelectedAccount && mobileSelectedFacilities.length > 0;

    // Menu icons mapping
    const getMenuIcon = (type) => {
        switch (type) {
            case 'hospital': return <LocalHospitalIcon />;
            case 'community-transfer': return <TransferWithinAStationIcon />;
            case 'deceased': return <PersonOffIcon />;
            case 'admissions': return <PersonAddIcon />;
            case 'overall': return <BarChartIcon />;
            case 'data-input': return <DataUsageIcon />;
            case 'residence-list': return <FormatListBulletedIcon />;
            default: return <DashboardIcon />;
        }
    };

    // Get available menu items based on selected account
    const getMenuItems = () => {
        const dashboardAccess = mobileSelectedAccount?.dashboardAccess || [];
        const items = [];

        // Residents List (main menu, after Data Input conceptually)
        items.push({
            label: "Residents List",
            path: setPreviousParams("/residence-list"),
            icon: getMenuIcon('residence-list'),
            type: 'residence-list'
        });

        if (_.includes(dashboardAccess, PAGE_TYPE.HOSPITAL)) {
            items.push({
                label: "Hospital",
                path: setPreviousParams("/dashboard/hospital"),
                icon: getMenuIcon('hospital'),
                type: 'hospital'
            });
        }

        if (_.includes(dashboardAccess, PAGE_TYPE.COMMUNITY_TRANSFER)) {
            items.push({
                label: "Community Transfer",
                path: setPreviousParams("/dashboard/community-transfer"),
                icon: getMenuIcon('community-transfer'),
                type: 'community-transfer',
                onClick: () => {
                    trackFeatureUsage(TUTORIAL_FEATURES.COMMUNITY_TRANSFERS).catch((error) => {
                        console.error('Failed to track community transfers usage:', error);
                    });
                    triggerTutorial(TUTORIAL_FEATURES.COMMUNITY_TRANSFERS).catch((error) => {
                        console.error('Failed to trigger community transfers tutorial:', error);
                    });
                }
            });
        }

        if (_.includes(dashboardAccess, PAGE_TYPE.DECEASED)) {
            items.push({
                label: "Deceased",
                path: setPreviousParams("/dashboard/deceased"),
                icon: getMenuIcon('deceased'),
                type: 'deceased',
                onClick: () => {
                    trackFeatureUsage(TUTORIAL_FEATURES.DECEASED_TRANSFERS).catch((error) => {
                        console.error('Failed to track deceased transfers usage:', error);
                    });
                    triggerTutorial(TUTORIAL_FEATURES.DECEASED_TRANSFERS).catch((error) => {
                        console.error('Failed to trigger deceased transfers tutorial:', error);
                    });
                }
            });
        }

        if (_.includes(dashboardAccess, PAGE_TYPE.ADMISSION)) {
            items.push({
                label: "Admissions",
                path: setPreviousParams("/dashboard/admissions"),
                icon: getMenuIcon('admissions'),
                type: 'admissions',
                onClick: () => {
                    trackFeatureUsage(TUTORIAL_FEATURES.ADMISSIONS).catch((error) => {
                        console.error('Failed to track admissions usage:', error);
                    });
                    triggerTutorial(TUTORIAL_FEATURES.ADMISSIONS).catch((error) => {
                        console.error('Failed to trigger admissions tutorial:', error);
                    });
                }
            });
        }

        if (_.includes(dashboardAccess, PAGE_TYPE.OVERALL)) {
            items.push({
                label: "Overall",
                path: setPreviousParams("/dashboard/overall"),
                icon: getMenuIcon('overall'),
                type: 'overall',
                onClick: () => {
                    trackFeatureUsage(TUTORIAL_FEATURES.OVERALL).catch((error) => {
                        console.error('Failed to track overall usage:', error);
                    });
                    triggerTutorial(TUTORIAL_FEATURES.OVERALL).catch((error) => {
                        console.error('Failed to trigger overall tutorial:', error);
                    });
                }
            });
        }

        return items;
    };

    // Update loading for all dashboards
    const updateLoadingToAllDashboard = useCallback(
        async (value) => {
            await dispatch(setHospitalLoading(value));
            await dispatch(setOverallLoading(value));
            await dispatch(setDescentLoading(value));
            await dispatch(setCommunityLoading(value));
            await dispatch(setAdmissionLoading(value));
        },
        [dispatch]
    );

    // Handle account selection
    const handleAccountSelect = async (account) => {
        await updateLoadingToAllDashboard(true);
        await new Promise(resolve => setTimeout(resolve, 0));
        await dispatch(setIsComparisonRangeSet(true));
        await dispatch({ type: SET_FACILITIES, payload: [] });
        await dispatch(removeAllActiveFacilities());
        await sessionStorage.removeItem("facilityId");
        setShowAccountDialog(false);
        dispatch(setFilterDateUpdate(true));
        dispatch(setFacilityLoaded(false));
        dispatch(setDefaultHospitalData());
        dispatch(setDefaultAdmissionData());
        dispatch(setDefaultCommunityTransferData());
        dispatch(setDefaultDeceasedData());
        dispatch(setDefaultOverAllData());
        dispatch(setResetComparison(true));
        dispatch(setEndDateOfADT(null));
        dispatch(setPercentageBy(percentageByEnum.censusAverage));
        dispatch(setManuallyClickComparison(null));
        await dispatch(setRemoveCompareFilter(false));
        await dispatch(setSelectedAccount(account));
        resetAllCards(dispatch);
        dispatch({ type: SET_ACCOUNTS, payload: "refreshing" });

        // Reset mobile state
        setMobileSelectedAccount(account);
        setMobileSelectedFacilities([]);
    };

    // Handle facility selection
    const handleFacilitySelect = (facility) => {
        const facilityId = facility._id;
        if (mobileSelectedFacilities.includes(facilityId)) {
            setMobileSelectedFacilities(mobileSelectedFacilities.filter(id => id !== facilityId));
        } else {
            setMobileSelectedFacilities([...mobileSelectedFacilities, facilityId]);
        }
    };

    // Handle facility selection confirmation
    const handleFacilityConfirm = () => {
        dispatch(setRemoveComparisonRanges([]));
        // Use utility function to update Redux and sync localStorage (multi-tab safe)
        setFacilityId(mobileSelectedFacilities, { updateLocalStorage: true });
        dispatch(setFilterDateUpdate(true));

        // Check if comparing against different facility
        if (_.isEqual(_.sortBy(mobileSelectedFacilities), _.sortBy(activeFacilitiesComparison))) {
            dispatch(setcomparingAgainstDifferentFacility(false));
        } else {
            dispatch(setcomparingAgainstDifferentFacility(true));
        }

        dispatch({ type: SET_FACILITIES, payload: "refreshing" });
        dispatch(setIsComparisonRangeSet(true));
        const facilityIdToSync = mobileSelectedFacilities.length > 0 ? mobileSelectedFacilities[0] : null;
        setParamToURL([{ key: "facilityId", value: facilityIdToSync }]);
        dispatch(setResetComparison(true));

        setShowFacilityDialog(false);
    };

    // Handle select all facilities
    const handleSelectAllFacilities = () => {
        if (selectAllFacilities) {
            setMobileSelectedFacilities([]);
            setSelectAllFacilities(false);
        } else {
            setMobileSelectedFacilities(filteredFacilities.map(fac => fac._id));
            setSelectAllFacilities(true);
        }
    };

    // Handle navigation
    const handleNavigate = (path) => {
        setInitialLoad(false);
        setHasCompletedSetup(true);
        // Add openFrom=mobile parameter to the path
        const separator = path.includes('?') ? '&' : '?';
        const pathWithMobileParam = `${path}${separator}openFrom=mobile`;
        navigate(pathWithMobileParam);
        onClose(); // Close the sidebar after navigation
    };

    // Check if menu item is active
    const isMenuItemActive = (path) => {
        // Remove query parameters for comparison
        const cleanPath = path.split('?')[0];
        const currentPath = location.pathname;

        // Handle exact matches and sub-routes
        if (cleanPath === '/data-input') {
            return currentPath === '/data-input';
        }
        if (cleanPath === '/residence-list') {
            return currentPath === '/residence-list';
        }

        // For dashboard routes, check if current path starts with the menu path
        if (cleanPath.startsWith('/dashboard/')) {
            return currentPath.startsWith(cleanPath);
        }

        return currentPath === cleanPath;
    };

    // Handle logout
    const handleLogout = () => {
        signOut(navigate, () => {
            timeLogControl?.stopLogging?.();
        });
    };

    // Filter accounts based on search
    const filteredAccounts = React.useMemo(() => {
        return Array.isArray(accounts) ? accounts.filter(acc =>
            acc.name?.toLowerCase().includes(accountSearchText?.toLowerCase())
        ) : [];
    }, [accounts, accountSearchText]);

    // Filter facilities based on search
    const filteredFacilities = React.useMemo(() => {
        return Array.isArray(facilities) ? facilities.filter(fac =>
            fac.name?.toLowerCase().includes(facilitySearchText?.toLowerCase())
        ) : [];
    }, [facilities, facilitySearchText]);

    // Update select all state when facilities are selected
    useEffect(() => {
        if (mobileSelectedFacilities.length === filteredFacilities.length && filteredFacilities.length > 0) {
            setSelectAllFacilities(true);
        } else {
            setSelectAllFacilities(false);
        }
    }, [mobileSelectedFacilities, filteredFacilities]);

    return (
        <Drawer
            anchor="top"
            open={open}
            onClose={onClose}
            sx={{
                display: { xs: 'block', md: 'none' },
                zIndex: 1400, // Make sure Drawer is above the floating AI button (which is 1300)
                '& .MuiDrawer-paper': {
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: '#f5f5f5',
                    boxSizing: 'border-box',
                    zIndex: 1400, // Higher z-index to appear above other elements
                },
            }}
        >
            <Box sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                pt: 0,
                pb: 0.25
            }}>
                {/* Mobile Header Card */}
                <Card sx={{ m: 0, borderRadius: 0, mb: 0.25 }}>
                    <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, px: 1.5, paddingBottom: "10px !important" }}>
                        <TempLogo isMobile={true} />
                        <IconButton
                            onClick={onClose}
                            disabled={!hasCompletedSetup}
                            sx={{
                                color: hasCompletedSetup ? '#f44336' : '#ccc',
                                p: 0.25,
                                '&:hover': {
                                    backgroundColor: hasCompletedSetup ? 'rgba(244, 67, 54, 0.1)' : 'transparent'
                                },
                                '&.Mui-disabled': {
                                    color: '#ccc'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </CardContent>
                </Card>

                {/* Scrollable Content Area */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {/* Account Selection */}
                    <Card sx={{ m: 1, borderRadius: 2, mb: 0.75 }}>
                        <CardContent sx={{ py: 1, px: 1.5 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.25, fontSize: '10px', fontWeight: 'bold' }}>
                                ACCOUNT
                            </Typography>
                            <ListItemButton
                                onClick={() => setShowAccountDialog(true)}
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1.5,
                                    py: 0.5,
                                    px: 1,
                                    minHeight: 'auto',
                                    '&:hover': { backgroundColor: '#f5f5f5' }
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 28 }}>
                                    <BusinessIcon sx={{ color: '#4879f5', fontSize: 18 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={mobileSelectedAccount ? mobileSelectedAccount.name : "Choose Account"}
                                    primaryTypographyProps={{
                                        fontWeight: mobileSelectedAccount ? 'normal' : 'bold',
                                        color: mobileSelectedAccount ? 'text.primary' : '#4879f5',
                                        fontSize: '12px'
                                    }}
                                />
                                {/* {mobileSelectedAccount && (
                                    <Chip label="Demo" size="small" sx={{ backgroundColor: '#e3f2fd', height: '16px', fontSize: '8px' }} />
                                )} */}
                            </ListItemButton>
                        </CardContent>
                    </Card>

                    {/* Facility Selection */}
                    <Card sx={{ m: 1, borderRadius: 2, mb: 0.75 }}>
                        <CardContent sx={{ py: 1, px: 1.5 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.25, fontSize: '10px', fontWeight: 'bold' }}>
                                FACILITIES
                            </Typography>
                            <ListItemButton
                                onClick={() => mobileSelectedAccount && setShowFacilityDialog(true)}
                                disabled={!mobileSelectedAccount || facility === "refreshing"}
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1.5,
                                    py: 0.5,
                                    px: 1,
                                    minHeight: 'auto',
                                    '&:hover': { backgroundColor: mobileSelectedAccount ? '#f5f5f5' : 'transparent' },
                                    opacity: !mobileSelectedAccount ? 0.5 : 1,
                                    cursor: !mobileSelectedAccount ? 'not-allowed' : 'pointer'
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 28 }}>
                                    <LocalHospitalIcon sx={{ color: mobileSelectedAccount ? '#4879f5' : '#ccc', fontSize: 18 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        mobileSelectedFacilities.length > 0
                                            ? mobileSelectedFacilities.length === 1
                                                ? filteredFacilities.find(f => f._id === mobileSelectedFacilities[0])?.name || "1 facility selected"
                                                : `${filteredFacilities.find(f => f._id === mobileSelectedFacilities[0])?.name || 'Facility'}`
                                            : "Choose Facilities"
                                    }
                                    primaryTypographyProps={{
                                        fontWeight: mobileSelectedFacilities.length > 0 ? 'normal' : 'bold',
                                        color: mobileSelectedFacilities.length > 0 ? 'text.primary' : (mobileSelectedAccount ? '#4879f5' : '#ccc'),
                                        fontSize: '12px'
                                    }}
                                />
                                {mobileSelectedFacilities.length > 1 && (
                                    <Chip label={`+${mobileSelectedFacilities.length - 1}`} size="small" sx={{ backgroundColor: '#e3f2fd', height: '16px', fontSize: '8px' }} />
                                )}
                            </ListItemButton>
                        </CardContent>
                    </Card>

                    {/* Navigation Menu - Only show after facility selection */}
                    {showNavigation && (
                        <Card sx={{ m: 1, borderRadius: 2, mb: 0.75 }}>
                            <CardContent sx={{ py: 1, px: 1.5 }}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontSize: '10px', fontWeight: 'bold' }}>
                                    Main menu choose adt tab to view
                                </Typography>
                                <List sx={{ p: 0 }}>
                                    {getMenuItems().map((item, index) => {
                                        const isActive = isMenuItemActive(item.path) && !initialLoad;
                                        return (
                                            <ListItemButton
                                                key={index}
                                                onClick={() => {
                                                    if (item.onClick) {
                                                        item.onClick();
                                                    }
                                                    handleNavigate(item.path);
                                                }}
                                                sx={{
                                                    borderRadius: 2,
                                                    mb: 0.25,
                                                    py: 0.5,
                                                    backgroundColor: isActive ? '#4879f5' : 'transparent',
                                                    color: isActive ? 'white' : 'inherit',
                                                    '&:hover': {
                                                        backgroundColor: isActive ? '#3d6fd4' : '#f0f7ff'
                                                    },
                                                    transition: 'all 0.2s ease',
                                                    '& .MuiListItemIcon-root': {
                                                        color: isActive ? 'white' : 'inherit',
                                                        minWidth: 32
                                                    }
                                                }}
                                            >
                                                <ListItemIcon>
                                                    {item.icon}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={item.label}
                                                    primaryTypographyProps={{
                                                        fontWeight: isActive ? 600 : 500,
                                                        color: isActive ? 'white' : 'inherit'
                                                    }}
                                                />
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </CardContent>
                        </Card>
                    )}

                    {/* Account Selection Dialog */}
                    <Dialog
                        open={showAccountDialog}
                        onClose={() => setShowAccountDialog(false)}
                        maxWidth="sm"
                        fullWidth
                        sx={{ zIndex: 1500 }}
                        PaperProps={{
                            sx: { borderRadius: 3, m: 2 }
                        }}
                    >
                        <DialogTitle>
                            <Typography variant="h6">Select Account</Typography>
                            <Typography variant="body2" color="text.secondary">
                                ({filteredAccounts.length} accounts)
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            <TextField
                                fullWidth
                                placeholder="Search account"
                                size="small"
                                value={accountSearchText}
                                onChange={(e) => setAccountSearchText(e.target.value)}
                                sx={{ mb: 2 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                                {filteredAccounts.map((acc) => (
                                    <ListItemButton
                                        key={acc._id}
                                        onClick={() => handleAccountSelect(acc)}
                                        selected={mobileSelectedAccount?._id === acc._id}
                                        sx={{
                                            borderRadius: 2,
                                            mb: 1,
                                            '&.Mui-selected': {
                                                backgroundColor: '#4879f5',
                                                color: 'white',
                                                '&:hover': { backgroundColor: '#3d6fd4' }
                                            }
                                        }}
                                    >
                                        <ListItemIcon>
                                            <Box
                                                sx={{
                                                    backgroundColor: "#d9d9d9",
                                                    width: "6px",
                                                    height: "6px",
                                                    borderRadius: "50%",
                                                }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText primary={acc.name} />
                                    </ListItemButton>
                                ))}
                            </List>
                        </DialogContent>
                    </Dialog>

                    {/* Facility Selection Dialog */}
                    <Dialog
                        open={showFacilityDialog}
                        onClose={() => setShowFacilityDialog(false)}
                        maxWidth="sm"
                        fullWidth
                        sx={{ zIndex: 1500 }}
                        PaperProps={{
                            sx: { borderRadius: 3, m: 2 }
                        }}
                    >
                        <DialogTitle>
                            <Typography variant="h6">Select Facilities</Typography>
                            <Typography variant="body2" color="text.secondary">
                                ({filteredFacilities.length} facilities)
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            <TextField
                                fullWidth
                                placeholder="Search facility"
                                size="small"
                                value={facilitySearchText}
                                onChange={(e) => setFacilitySearchText(e.target.value)}
                                sx={{ mb: 2 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {/* Select All Option */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                <Checkbox
                                    checked={selectAllFacilities}
                                    onChange={handleSelectAllFacilities}
                                    sx={{
                                        color: '#4879f5',
                                        '&.Mui-checked': { color: '#4879f5' }
                                    }}
                                />
                                <Typography variant="body2" sx={{ ml: 1 }}>
                                    Select All ({filteredFacilities.length})
                                </Typography>
                            </Box>

                            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                                {filteredFacilities.map((fac) => (
                                    <ListItemButton
                                        disableRipple
                                        dense
                                        key={fac._id}
                                        onClick={() => handleFacilitySelect(fac)}
                                        selected={mobileSelectedFacilities.includes(fac._id)}
                                        sx={{
                                            borderRadius: 2,
                                            mb: 0.5,
                                            py: 0.5,
                                            '&.Mui-selected': {
                                                backgroundColor: '#4879f5',
                                                color: 'white',
                                                '&:hover': { backgroundColor: '#3d6fd4' }
                                            }
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <Checkbox
                                                checked={mobileSelectedFacilities.includes(fac._id)}
                                                onChange={() => handleFacilitySelect(fac)}
                                                size="small"
                                                sx={{
                                                    color: mobileSelectedFacilities.includes(fac._id) ? 'white' : 'inherit',
                                                    '&.Mui-checked': {
                                                        color: mobileSelectedFacilities.includes(fac._id) ? 'white' : 'inherit'
                                                    },
                                                    '& .MuiSvgIcon-root': {
                                                        color: mobileSelectedFacilities.includes(fac._id) ? 'white' : 'inherit'
                                                    }
                                                }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={fac?.name}
                                            primaryTypographyProps={{
                                                color: mobileSelectedFacilities.includes(fac._id) ? 'white' : 'inherit'
                                            }}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        </DialogContent>
                        <DialogActions sx={{ p: 2 }}>
                            <Button onClick={() => setShowFacilityDialog(false)}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleFacilityConfirm}
                                variant="contained"
                                disabled={mobileSelectedFacilities.length === 0}
                                sx={{ backgroundColor: '#4879f5', '&:hover': { backgroundColor: '#3d6fd4' } }}
                            >
                                Confirm ({mobileSelectedFacilities.length})
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>

                {/* User Profile Card */}
                <Card 
                    onClick={handleProfileClick}
                    sx={{ 
                        m: 1, 
                        mb: 0.25, 
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                            backgroundColor: '#f5f5f5'
                        }
                    }}
                >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1.5, paddingBottom: '3px !important' }}>
                        <Avatar
                            src={`/assets/avatars/${auth.avatar}.png`}
                            alt={auth.fullName}
                            sx={{ width: 28, height: 28 }}
                        />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight="600" sx={{ fontSize: '11px' }}>
                                {auth.fullName}
                            </Typography>
                            <Chip
                                label={auth.type === 'superAdmin' ? 'Super Admin' : auth.type === 'admin' ? 'Admin' : 'User'}
                                size="small"
                                sx={{
                                    mt: 0.125,
                                    backgroundColor: '#4879f5',
                                    color: 'white',
                                    fontSize: '7px',
                                    height: '14px'
                                }}
                            />
                        </Box>
                    </CardContent>
                </Card>

                {/* Profile menu popover for mobile */}
                <Menu
                    anchorEl={profileAnchorEl}
                    open={profileOpen}
                    onClose={handleProfileClose}
                    onClick={handleProfileClose}
                    sx={{ zIndex: 1500 }}
                    PaperProps={{
                        elevation: 3,
                        sx: {
                            width: '200px',
                            borderRadius: '12px',
                            mt: -1,
                            '& .MuiMenuItem-root': {
                                fontSize: '13px',
                                py: 1,
                                px: 2,
                            }
                        }
                    }}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                >
                    <MenuItem onClick={() => handleNavigate(setPreviousParams("/settings/profile"))}>
                        <ListItemIcon sx={{ minWidth: '28px !important' }}>
                            <PersonIcon fontSize="small" sx={{ color: '#4879f5' }} />
                        </ListItemIcon>
                        Manage Profile
                    </MenuItem>
                    <MenuItem onClick={() => handleNavigate(setPreviousParams("/tutorials"))}>
                        <ListItemIcon sx={{ minWidth: '28px !important' }}>
                            <HelpIcon fontSize="small" sx={{ color: '#4879f5' }} />
                        </ListItemIcon>
                        Tutorials
                    </MenuItem>
                </Menu>

                {/* Logout Section */}
                <Card sx={{ m: 1, mb: 0.25, borderRadius: 2 }}>
                    <CardContent sx={{ p: 0.75, '&:last-child': { pb: 0.75 } }}>
                        <ListItemButton
                            onClick={handleLogout}
                            sx={{
                                borderRadius: 2,
                                py: 0.5,
                                px: 1.5,
                                minHeight: 'auto',
                                '&:hover': { backgroundColor: '#ffebee' },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                <LogoutIcon sx={{ color: '#f44336', fontSize: 16 }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Logout"
                                primaryTypographyProps={{
                                    fontWeight: 500,
                                    color: '#f44336',
                                    fontSize: '12px'
                                }}
                            />
                        </ListItemButton>
                    </CardContent>
                </Card>
            </Box>
        </Drawer>
    );
};

export default MobileApp; 